import { useCallback, useEffect, useState } from 'react';
import { MapRef } from 'react-map-gl/mapbox';
import { PropertyDetailsResponseData } from '../../../models/details.models.tsx';

// Controls for map view boundaries
const BOUNDS_THRESHOLD = 0.05; // Maximum allowed deviation from address point in long/lat (+/-)
const MIN_ZOOM = 15; // Min zoom level, lower number = farther out
const TARGET_ZOOM = 18; // Target zoom level
const ANIMATION_DURATION = 2000; // Duration of return to starting bounds animation
const CHECK_VIEW_BOUNDS_INTERVAL = 1000; // how long until we check if the map is out of bounds

interface UseMapViewStateProps {
  addressDetails?: PropertyDetailsResponseData;
  mapRef: React.RefObject<MapRef>;
  initialLongitude: number;
  initialLatitude: number;
  initialZoom: number;
}

interface ViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

export const useMapViewState = ({
  addressDetails,
  mapRef,
  initialLongitude,
  initialLatitude,
  initialZoom,
}: UseMapViewStateProps) => {
  const [viewState, setViewState] = useState({
    longitude: initialLongitude,
    latitude: initialLatitude,
    zoom: initialZoom,
  } as ViewState);

  const checkViewBounds = useCallback(() => {
    if (!addressDetails) return;

    const centerLat = addressDetails.latitude;
    const centerLng = addressDetails.longitude;

    const latDiff = Math.abs(viewState.latitude - centerLat);
    const lngDiff = Math.abs(viewState.longitude - centerLng);
    const zoomTooLow = viewState.zoom < MIN_ZOOM;
    const outOfBounds =
      latDiff > BOUNDS_THRESHOLD || lngDiff > BOUNDS_THRESHOLD;

    if (outOfBounds || zoomTooLow) {
      const targetZoom =
        outOfBounds || zoomTooLow ? TARGET_ZOOM : viewState.zoom;
      const targetLat =
        outOfBounds || zoomTooLow ? centerLat : viewState.latitude;
      const targetLng =
        outOfBounds || zoomTooLow ? centerLng : viewState.longitude;

      mapRef.current?.flyTo({
        center: [targetLng, targetLat],
        zoom: targetZoom,
        duration: ANIMATION_DURATION,
      });
    }
  }, [addressDetails, viewState, mapRef]);

  useEffect(() => {
    setViewState({
      longitude: addressDetails?.longitude || initialLongitude,
      latitude: addressDetails?.latitude || initialLatitude,
      zoom: initialZoom,
    });
  }, [addressDetails, initialLongitude, initialLatitude, initialZoom]);

  useEffect(() => {
    const timeoutId = setTimeout(checkViewBounds, CHECK_VIEW_BOUNDS_INTERVAL);
    return () => clearTimeout(timeoutId);
  }, [checkViewBounds]);

  const handleViewStateChange = useCallback((evt: { viewState: ViewState }) => {
    setViewState(evt.viewState);
  }, []);

  return {
    viewState,
    handleViewStateChange,
  };
};
